import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { CreditCard, Package, Wallet, ArrowRight, ArrowDown } from "lucide-react";
import LegalShell from "../components/marketing/LegalShell";
import { fadeUp } from "../components/marketing/Blocks";

const OVERVIEW = [
  { icon: CreditCard, title: "Payment Issue", desc: "Transaction failed or was duplicated.", accent: "text-red-400", bg: "bg-red-500/15" },
  { icon: Package, title: "Shipment Adjustment", desc: "Eligible shipping-related billing issue.", accent: "text-shipany-cyan", bg: "bg-shipany-cyan/15" },
  { icon: Wallet, title: "Wallet Credit", desc: "Approved amounts may be credited according to applicable policy.", accent: "text-[#10B981]", bg: "bg-[#10B981]/15" },
];

const STEPS = [
  ["01", "Request", "Submit your refund request."],
  ["02", "Verification", "Our team reviews the transaction/shipment."],
  ["03", "Decision", "Eligibility is determined under the applicable policy."],
  ["04", "Processing", "Approved refunds are processed."],
  ["05", "Credit", "Amount is credited through the applicable payment method."],
];

const SECTIONS = [
  { id: "wallet-refunds", h: "Wallet & Payment Refunds", body: [
    "ShipAny uses a prepaid wallet for shipping charges. Eligible refunds are credited to your ShipAny wallet. Wallet top-up transactions themselves are refundable only in limited cases such as duplicate or failed transactions, subject to verification.",
  ]},
  { id: "shipping-adjustments", h: "Shipping Charge Adjustments", body: [
    "Shipments cancelled before courier pickup are refunded in full to your wallet after the courier confirms cancellation — most confirmations are instant.",
    "If a billed charge is found to be incorrect after review against courier records, the eligible difference is adjusted to your wallet.",
  ]},
  { id: "duplicate", h: "Duplicate Transactions", body: [
    "If a wallet top-up or charge is duplicated due to a technical error, the duplicate amount is refunded after verification of the transaction records.",
  ]},
  { id: "failed-payments", h: "Failed Payments", body: [
    "If a payment is debited by your bank or UPI but the wallet is not credited, the amount is either credited to your wallet or refunded through the original payment method after the payment gateway confirms the failure.",
  ]},
  { id: "rto", h: "RTO Charges", body: [
    "RTO (Return to Origin) shipments are billed per the courier partner's rate card. RTO charges applied by the courier are generally not refundable; any refundable component is credited to your wallet.",
  ]},
  { id: "cod-adjustments", h: "COD-Related Adjustments", body: [
    "COD remittances and adjustments follow the courier partner's settlement cycles. Discrepancies in COD collection or settlement can be raised with support for reconciliation against courier records.",
  ]},
  { id: "non-refundable", h: "Non-Refundable Charges", body: [
    "Charges for services already rendered — such as completed pickups, in-transit shipments, delivered shipments and courier-applied RTO charges — are not refundable except where required by applicable law.",
  ]},
  { id: "processing-time", h: "Refund Processing Time", body: [
    "Wallet credits are applied once the refund is approved and, where relevant, confirmed by the courier. Refunds to original payment methods depend on the payment gateway and bank processing timelines.",
  ]},
  { id: "how-to-request", h: "How to Request a Refund", body: [
    "Raise a support ticket from your ShipAny dashboard or contact us via the Contact page. Please provide the relevant details so we can verify quickly:",
  ], list: ["Shipment/AWB number", "Transaction ID", "Registered email/phone", "Reason for request", "Supporting documents where applicable"] },
];

const TOC = SECTIONS.map((s) => [s.h, s.id]);

export default function RefundPolicy() {
  return (
    <LegalShell testId="refund-page" kicker="Refunds & Payments" title="Clear Refund Guidelines"
      sub="We believe payment and refund processes should be transparent and easy to understand."
      updated="June 2026" toc={TOC} sections={SECTIONS}>
      <div className="mt-8 grid gap-4 sm:grid-cols-3" data-testid="refund-overview-cards">
        {OVERVIEW.map((c, i) => (
          <motion.div key={c.title} {...fadeUp} transition={{ duration: 0.5, delay: i * 0.08 }}
            data-testid={`refund-card-${c.title.toLowerCase().replace(/[^a-z]+/g, "-")}`}
            className="rounded-2xl border border-white/10 bg-white/[0.04] p-6">
            <span className={`flex h-11 w-11 items-center justify-center rounded-xl ${c.bg}`}><c.icon className={`h-5 w-5 ${c.accent}`} /></span>
            <h3 className="font-display mt-4 text-sm font-bold text-white">{c.title}</h3>
            <p className="mt-1.5 text-xs leading-relaxed text-slate-400">{c.desc}</p>
          </motion.div>
        ))}
      </div>

      <div className="mt-10 rounded-3xl border border-white/10 bg-white/[0.04] p-6 sm:p-8" data-testid="refund-process-timeline">
        <h3 className="font-display text-base font-bold text-white">Refund Process</h3>
        <div className="mt-6 grid gap-4 sm:grid-cols-5">
          {STEPS.map(([n, t, d], i) => (
            <motion.div key={n} {...fadeUp} transition={{ duration: 0.45, delay: i * 0.1 }} className="relative text-center"
              data-testid={`refund-step-${i + 1}`}>
              <span className="mono mx-auto flex h-10 w-10 items-center justify-center rounded-full border border-shipany-cyan/40 bg-shipany-cyan/10 text-xs font-bold text-shipany-cyan">{n}</span>
              <p className="mt-2.5 text-xs font-bold text-white">{t}</p>
              <p className="mt-1 text-[10px] leading-relaxed text-slate-400">{d}</p>
              {i < STEPS.length - 1 && <ArrowDown className="mx-auto mt-2 h-3.5 w-3.5 text-slate-600 sm:hidden" />}
            </motion.div>
          ))}
        </div>
      </div>

      <motion.div {...fadeUp} className="mt-8 flex flex-col items-start justify-between gap-4 rounded-2xl border border-[#FF9933]/30 bg-[#FF9933]/5 p-6 sm:flex-row sm:items-center"
        data-testid="refund-help-cta">
        <div>
          <h3 className="font-display text-base font-bold text-white">Need Help With a Refund?</h3>
          <p className="mt-1 text-xs text-slate-400">Share your AWB or transaction ID and our team will review it.</p>
        </div>
        <Link to="/contact" data-testid="refund-contact-btn"
          className="flex shrink-0 items-center gap-2 rounded-xl bg-gradient-to-r from-[#FF9933] to-[#FF8000] px-6 py-3 font-display text-sm font-bold text-[#071B3A] transition-all hover:shadow-[0_8px_30px_rgba(255,153,51,0.45)]">
          Contact ShipAny Support <ArrowRight className="h-4 w-4" />
        </Link>
      </motion.div>
    </LegalShell>
  );
}
